import { ACTIONS, RESOURCES, type Action, type PermissionSet, type Resource, type Role } from '@buildbee/contracts';
import { anyMatch, ROLE_DEFAULTS } from './defaults';

/**
 * Role hierarchy and per-company role policies.
 *
 *   owner > admin > pm · sales > field > sub        (client: portal only, outside the ladder's inheritance)
 *
 * A higher role includes every tick of the roles below it unless that tick is removed. BuildBee's
 * defaults already remove some (sales does not get field's site work, field does not get the
 * subcontractor-only bid and lien waiver cells): those are computed from ROLE_DEFAULTS, so a
 * workspace without policies gets exactly the built-in role defaults.
 *
 * A company changes a role with a role policy: `allow` adds ticks, `deny` removes them. Added ticks
 * flow up the ladder (a tick added to Field also reaches PM, Sales and Admin unless they removed
 * it); removed ticks do not flow anywhere. Policies are stored as `permission_template` rows named
 * `role:<role>` (see ROLE_POLICY_PREFIX).
 */

export type CellKey = `${Resource}:${Action}`;

const RESOURCE_SET: ReadonlySet<string> = new Set(RESOURCES);
const ACTION_SET: ReadonlySet<string> = new Set(ACTIONS);

export function cellKey(resource: Resource, action: Action): CellKey {
  return `${resource}:${action}`;
}

/** Split a concrete cell (`deal:update`); null for wildcards or unknown names. */
export function parseCell(cell: string): { resource: Resource; action: Action } | null {
  const i = cell.indexOf(':');
  if (i <= 0) return null;
  const resource = cell.slice(0, i);
  const action = cell.slice(i + 1);
  if (!RESOURCE_SET.has(resource) || !ACTION_SET.has(action)) return null;
  return { resource: resource as Resource, action: action as Action };
}

/** Every concrete cell a permission pattern covers (`invoice:*` -> invoice:view, invoice:create, ...). */
export function expandPattern(pattern: string): CellKey[] {
  const out: CellKey[] = [];
  for (const r of RESOURCES) for (const a of ACTIONS) if (anyMatch([pattern], r, a)) out.push(cellKey(r, a));
  return out;
}

/** Rank on the ladder. Nobody can edit or grant at or above their own rank (owners excepted). */
export const ROLE_LEVEL: Readonly<Record<Role, number>> = {
  owner: 5,
  admin: 4,
  pm: 3,
  sales: 3,
  field: 2,
  sub: 1,
  client: 0,
};

/** Direct children whose ticks a role includes. */
export const ROLE_INHERITS: Readonly<Record<Role, readonly Role[]>> = {
  owner: [],
  admin: ['pm', 'sales'],
  pm: ['field'],
  sales: ['field'],
  field: ['sub'],
  sub: [],
  client: [],
};

/** The ladder as shown on the Roles page, top to bottom. Roles on one rung share a rank. */
export const ROLE_LADDER: readonly (readonly Role[])[] = [['owner'], ['admin'], ['pm', 'sales'], ['field'], ['sub'], ['client']];

/** Only owners ever have these, whatever a policy or a per-person template says. */
export const OWNER_ONLY_CELLS: ReadonlySet<string> = new Set<CellKey>(['billing:manage', 'workspace:delete']);

/** Every team role needs these to open the workspace at all; they cannot be removed. */
export const REQUIRED_CELLS: ReadonlySet<string> = new Set<CellKey>(['workspace:view']);

/** Roles a company can change. Owner always has everything; client is fixed to the portal. */
export const POLICY_ROLES = ['admin', 'pm', 'sales', 'field', 'sub'] as const satisfies readonly Role[];
export type PolicyRole = (typeof POLICY_ROLES)[number];
export function isPolicyRole(role: Role): role is PolicyRole {
  return (POLICY_ROLES as readonly Role[]).includes(role);
}

/** Roles where "only their own records" can be switched on or off for the whole role. */
export const OWN_RECORDS_ROLES: ReadonlySet<Role> = new Set<Role>(['field', 'sub']);

/** Name of the permission_template row that stores a role's policy. */
export const ROLE_POLICY_PREFIX = 'role:';
export function rolePolicyName(role: Role): string {
  return `${ROLE_POLICY_PREFIX}${role}`;
}
export function isRolePolicyName(name: string): boolean {
  return name.trim().toLowerCase().startsWith(ROLE_POLICY_PREFIX);
}

/** A company's changes per role (permission_template.permissions of the `role:<role>` rows). */
export type RolePolicies = Partial<Record<Role, PermissionSet | null | undefined>>;

type CompiledPolicy = { allow: ReadonlySet<string>; deny: ReadonlySet<string>; ownRecordsOnly?: boolean };

/** Every role above `role` (direct and indirect parents). */
export function ancestorsOf(role: Role): Role[] {
  const out = new Set<Role>();
  const visit = (r: Role) => {
    for (const parent of Object.keys(ROLE_INHERITS) as Role[]) {
      if (ROLE_INHERITS[parent].includes(r) && !out.has(parent)) {
        out.add(parent);
        visit(parent);
      }
    }
  };
  visit(role);
  return [...out];
}

function defaultAllows(role: Role, resource: Resource, action: Action): boolean {
  return anyMatch(ROLE_DEFAULTS[role].allow, resource, action);
}

function defaultEffective(role: Role, resource: Resource, action: Action): boolean {
  if (role === 'owner') return true;
  if (OWNER_ONLY_CELLS.has(cellKey(resource, action))) return false;
  return defaultAllows(role, resource, action) && !anyMatch(ROLE_DEFAULTS[role].deny, resource, action);
}

/**
 * Ticks a role does not take from the roles below it in BuildBee's defaults: a child has it, the
 * role's own default does not. Computed, so the defaults stay the single source of truth.
 */
const excludedMemo = new Map<string, boolean>();
export function excludedByDefault(role: Role, resource: Resource, action: Action): boolean {
  const key = `${role}|${resource}:${action}`;
  let hit = excludedMemo.get(key);
  if (hit === undefined) {
    hit = !defaultAllows(role, resource, action) && ROLE_INHERITS[role].some((child) => defaultEffective(child, resource, action));
    excludedMemo.set(key, hit);
  }
  return hit;
}

export type RoleEvaluator = {
  /** The role's own decision for a cell: defaults, inheritance and the company's policy. */
  grants(role: Role, resource: Resource, action: Action): boolean;
  /** Denied whatever a per-person template says (owner-only cells; built-in denies not lifted by a policy). */
  hardDenied(role: Role, resource: Resource, action: Action): boolean;
  /** Role-level "only their own records" (policy first, then the default). */
  ownRecordsOnly(role: Role): boolean | undefined;
  policy(role: Role): CompiledPolicy | undefined;
};

function compile(policies: RolePolicies | null | undefined): Partial<Record<Role, CompiledPolicy>> {
  const out: Partial<Record<Role, CompiledPolicy>> = {};
  if (!policies) return out;
  for (const role of POLICY_ROLES) {
    const p = policies[role];
    if (!p) continue;
    // Policies hold concrete cells only; anything else (a wildcard, an unknown name) is ignored.
    const allow = new Set((p.allow ?? []).filter((c) => parseCell(c) && !OWNER_ONLY_CELLS.has(c)));
    const deny = new Set((p.deny ?? []).filter((c) => parseCell(c) && !REQUIRED_CELLS.has(c)));
    for (const c of deny) allow.delete(c);
    out[role] = { allow, deny, ...(OWN_RECORDS_ROLES.has(role) && p.ownRecordsOnly !== undefined ? { ownRecordsOnly: p.ownRecordsOnly } : {}) };
  }
  return out;
}

/** Bind a company's role policies once; decisions are memoized (at most roles x cells entries). */
export function createRoleEvaluator(policies?: RolePolicies | null): RoleEvaluator {
  const compiled = compile(policies);
  const memo = new Map<string, boolean>();

  const hardDenied = (role: Role, resource: Resource, action: Action): boolean => {
    if (role === 'owner') return false;
    const cell = cellKey(resource, action);
    if (OWNER_ONLY_CELLS.has(cell)) return true;
    return anyMatch(ROLE_DEFAULTS[role].deny, resource, action) && !compiled[role]?.allow.has(cell);
  };

  const grants = (role: Role, resource: Resource, action: Action): boolean => {
    if (role === 'owner') return true;
    const key = `${role}|${resource}:${action}`;
    const hit = memo.get(key);
    if (hit !== undefined) return hit;
    const cell = cellKey(resource, action);
    const p = compiled[role];
    let result: boolean;
    if (p?.deny.has(cell)) result = false;
    else if (defaultAllows(role, resource, action) || p?.allow.has(cell)) result = true;
    else if (excludedByDefault(role, resource, action)) result = false;
    else result = ROLE_INHERITS[role].some((child) => grants(child, resource, action) && !hardDenied(child, resource, action));
    memo.set(key, result);
    return result;
  };

  return {
    grants,
    hardDenied,
    ownRecordsOnly: (role) => compiled[role]?.ownRecordsOnly ?? ROLE_DEFAULTS[role].ownRecordsOnly,
    policy: (role) => compiled[role],
  };
}

/** Effective role-level decision (what the Roles matrix shows for a role column). */
export function roleHas(evaluator: RoleEvaluator, role: Role, resource: Resource, action: Action): boolean {
  return evaluator.grants(role, resource, action) && !evaluator.hardDenied(role, resource, action);
}
